import { useState } from "react";
import { Moon } from "lucide-react";

import { useAuth } from "../../Services/AuthContext";
import { useGoingOut } from "../../Services/GoingOutContext";
import GoingOutPicker from "./GoingOutPicker";
import { STATE_LABELS } from "./goingOutLabels";
import type { GoingOutStatus } from "../../data/types";
import "../../Styles/GoingOut.css";

type GoingOutStatusControlProps = {
  /** Var kontrollen sitter. Styr bara utseendet. */
  variant?: "menu" | "profile";
  /** Kallas efter ett sparat svar — menyn använder det för att stänga sig. */
  onAnswered?: () => void;
};

/**
 * Kvällssvaret där det går att ändra: i menyn bakom avataren och på den
 * egna profilen.
 *
 * Motsatsen till chippet på kartan. Kartan ställer frågan och slutar sedan
 * fråga; här står svaret kvar hela natten, för att det ska finnas ett
 * ställe att ändra sig på när planerna spricker klockan nio.
 *
 * Har man inte svarat än står frågan här också — det är samma fråga, och
 * den som öppnat menyn har redan valt att titta.
 */
export default function GoingOutStatusControl({
  variant = "menu",
  onAnswered,
}: GoingOutStatusControlProps) {
  const { isAuthenticated } = useAuth();
  const { state, isBusy, answer } = useGoingOut();

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  if (!isAuthenticated || !state) return null;

  const mine = state.me;

  const choose = async (status: GoingOutStatus) => {
    await answer(status);
    setIsPickerOpen(false);
    onAnswered?.();
  };

  return (
    <div className={`going-out-control going-out-control--${variant}`}>
      <span className="going-out-control-label">Tonight</span>

      {isPickerOpen ? (
        <GoingOutPicker
          current={mine?.status ?? null}
          disabled={isBusy}
          onAnswer={choose}
          onCancel={() => setIsPickerOpen(false)}
        />
      ) : (
        <button
          type="button"
          className={`going-out-chip ${
            mine ? `going-out-chip--${mine.status.toLowerCase()}` : ""
          }`}
          onClick={() => setIsPickerOpen(true)}
          disabled={isBusy}
        >
          <Moon size={14} strokeWidth={2.2} />
          {/* Svarat: tillståndet, och att trycka på det öppnar väljaren.
              Obesvarat: frågan, precis som på kartan. */}
          {mine ? STATE_LABELS[mine.status] : "Going out tonight?"}
        </button>
      )}
    </div>
  );
}
